import {
  Activity,
  AlertTriangle,
  BrainCircuit,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  CloudCog,
  FileCheck2,
  FileText,
  FolderSync,
  Play,
  ShieldCheck,
  TriangleAlert,
  Workflow,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useDemoStore } from '../../store'
import { DemoNotice, PageTitle, StatusBadge } from './shared'

export function OverviewPage() {
  const { approvals, integrations } = useDemoStore()
  const pending = approvals.filter(item => item.status === 'pending').length
  const connected = integrations.filter(item => item.connected).length
  const navigate = useNavigate()

  return <>
    <PageTitle
      title="Deus Intus pilot, in command."
      copy="22 July 2026 · Tenant 001 · Read-only production rollout"
      actions={<button className="button button--gold" onClick={() => navigate('/app/workflows')}><Play size={16} /> Review workflows</button>}
    />
    <DemoNotice />
    <div className="metric-row">
      <article><div><Workflow size={19} /><span>Stage-1 workflows</span></div><strong>9</strong><p>Registered and enabled in seed data</p></article>
      <article><div><FileCheck2 size={19} /><span>Pilot gates</span></div><strong>{pending}</strong><p>Must close before real workflow claims</p></article>
      <article><div><BrainCircuit size={19} /><span>Live source readiness</span></div><strong>{connected}/7</strong><p>Private connections not yet deployed</p></article>
      <article><div><CircleDollarSign size={19} /><span>Pilot usage</span></div><strong>£0</strong><p>£400 monthly hard stop configured</p></article>
    </div>
    <div className="dashboard-grid">
      <section className="app-panel app-panel--wide">
        <div className="panel-heading"><div><h2>Next rollout gates</h2><p>Only work that materially advances the safe production pilot.</p></div><button className="text-button">Tenant 001 plan</button></div>
        <div className="priority-list">
          {[
            ['Configure Cloudflare Access', 'DAWN Control Plane', 'Required before remote API deployment', 'approval'],
            ['Create the private source register', 'Business Brain', 'Map approved Drive and Sheets references outside GitHub', 'review'],
            ['Apply D1 migrations and seed Tenant 001', 'DAWN Control Plane', 'Creates tenant, workflows, budgets and kill switches', 'approval'],
            ['Capture the 14-day manual baseline', 'Case Study Evidence', 'Required before time-saving or quality claims', 'warning'],
          ].map(([title, owner, meta, kind]) => <article key={title}><span className={`priority-icon priority-icon--${kind}`}>{kind === 'warning' ? <AlertTriangle size={17} /> : kind === 'review' ? <ShieldCheck size={17} /> : <FileCheck2 size={17} />}</span><div><strong>{title}</strong><small>{owner}</small></div><span>{meta}</span><ChevronRight size={17} /></article>)}
        </div>
      </section>
      <section className="app-panel">
        <div className="panel-heading"><div><h2>Control-plane status</h2><p>Implementation state, not simulated uptime.</p></div><StatusBadge tone="warn">Deployment pending</StatusBadge></div>
        <div className="health-list">
          <div><span><CheckCircle2 size={16} />Tenant configuration</span><strong>Ready</strong></div>
          <div><span><CheckCircle2 size={16} />D1 schema and seed</span><strong>Ready</strong></div>
          <div><span><CalendarClock size={16} />Scheduled workflows</span><strong>9 registered</strong></div>
          <div><span><CloudCog size={16} />Remote services</span><strong>Not deployed</strong></div>
          <div><span><FolderSync size={16} />Real source sync</span><strong>Not started</strong></div>
        </div>
      </section>
      <section className="app-panel app-panel--wide">
        <div className="panel-heading"><div><h2>Rollout timeline</h2><p>Current implementation and remaining production boundaries.</p></div><button className="text-button" onClick={() => navigate('/app/workflows')}>Open workflow registry</button></div>
        <div className="timeline-list">
          {[
            ['DAWN foundation', 'Public site and Command Centre implemented', 'Complete', '22 Jul'],
            ['Deus Intus Tenant 001', 'Tenant, modules, workflows and evidence contract defined', 'Complete', '22 Jul'],
            ['Control plane', 'Worker, D1, durable workflow, scheduler and API implemented', 'Complete', '22 Jul'],
            ['Secure deployment', 'Cloudflare Access, D1 provisioning and source secrets required', 'Blocked', 'Next'],
            ['First evidence run', 'Begins after source health and baseline gates pass', 'Queued', 'After deployment'],
          ].map(([title, copy, status, time]) => <article key={title}><span className={`timeline-dot timeline-dot--${status.toLowerCase()}`} /><div><strong>{title}</strong><small>{copy}</small></div><StatusBadge tone={status === 'Complete' ? 'good' : status === 'Blocked' ? 'warn' : 'neutral'}>{status}</StatusBadge><time>{time}</time></article>)}
        </div>
      </section>
      <section className="app-panel">
        <div className="panel-heading"><div><h2>Implemented pilot artefacts</h2><p>Repository-backed outputs ready for deployment review.</p></div></div>
        <div className="output-list">{[
          ['Tenant 001 configuration', 'Tenant policy', 'Approved'],
          ['15-workflow automation registry', 'Automation', 'Approved'],
          ['D1 control-plane schema', 'Infrastructure', 'Approved'],
          ['Case-study evidence contract', 'Measurement', 'Approved'],
          ['Private connector register', 'Security', 'Review'],
        ].map(([name, type, status]) => <article key={name}><FileText size={17} /><div><strong>{name}</strong><small>{type}</small></div><StatusBadge tone={status === 'Approved' ? 'good' : 'warn'}>{status}</StatusBadge></article>)}</div>
      </section>
    </div>
  </>
}
